import { Loader } from 'google-maps'
import gsap, { Power4 } from 'gsap'
import scroll from '../animations/scroll/locomotiveScroll'
import styles from '../map-style.json'
import { addClass, qs, qsa, removeClass } from '../utils'
import MarkerClusterer from '@googlemaps/markerclustererplus'

class WorldMap {
  constructor() {
    this.namespace = 'world-map'
  }

  beforeEnter({ next }) {
    this.map = null
    this.wrapper = qs('.c-filters', next.container)
    this.content = qs('[data-content]', this.wrapper)
    this.toggle = qs('[data-toggle]', this.wrapper)
    this.close = qs('[data-close]', this.wrapper)
    this.markers = []
    this.filtersOpen = false
    this.activeCategory = ''
    this.animating = false
    this.filters = []

    this.init()
    scroll.update()
  }

  beforeLeave() {
    this.removeListeners()
  }

  async fetchPosts() {
    const response = await fetch('/wp-json/rewild/v1/world-map', {
      headers : {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
       }
    })

    const posts = await response.json()

    const markers = posts.reduce((marker, post) => {

      if (post.custom?.marker_geopoint) {
        marker.push({
          id: post?.ID,
          title: post?.post_title,
          avatar: post?.logo?.sizes?.medium || '/app/themes/rewild-yourself/static/images/default.webp',
          slug: post?.slug,
          hash: post?.tag?.name,
          link: post?.link,
          description: post?.marker_description,
          label: post.category && post?.category?.name,
          icon: post.category && post?.category?.image,
          type: post.category && post.category?.name,
          typeSlug: post.category && post?.category?.slug,
          theme: post.category && post?.category?.color,
          position: {
            lng: post?.marker_geopoint?.lng,
            lat: post?.marker_geopoint?.lat
          }
        })
      }

      return marker
    }, [])

    return markers
  }

  async init() {
    const map = await this.initMap()
    this.map = map

    const posts = await this.fetchPosts()

    this.createMarkers(posts)
    this.createFilters(posts)

    this.initListeners()
    this.markerCluster = new MarkerClusterer(this.map, this.markers, {
      styles: [{
        width: 50,
        height: 50
      }],
      clusterClass: "c-cluster"
    })

    this.checkForQueryParameters()
  }

  createFilters(posts) {
    const filterList = qs('ul', this.content)

    posts.forEach((post, i) => {
      if (post.typeSlug && !this.filters.some(filter => filter === post.typeSlug)) {
        this.filters.push(post.typeSlug)

        filterList.innerHTML += `
        <li>
          <button class="${ i === 0 ? 'active' : null }" value="${post.typeSlug}">${post.type}</button>
          <span style="background-color: ${post.theme};"></span>
        </li>`
      }
    })

    filterList.innerHTML += `
      <li>
        <button value="">All</button>
      </li>`
  }

  createMarkers(posts) {
    posts.forEach(marker => {
      const { id, position, title, icon, type, typeSlug, slug } = marker

      const infoWindow = new google.maps.InfoWindow({
        content: this.createInfoWindow(marker)
      })

      const newMarker = new google.maps.Marker({
        position,
        title,
        icon
      })

      newMarker.infoWindow = infoWindow
      newMarker.id = id.toString()
      newMarker.slug = slug
      newMarker.type = type
      newMarker.typeSlug = typeSlug
      newMarker.setMap(this.map)
      newMarker.addListener('click', () => {
        this.markers.forEach(marker => {
          marker.infoWindow.close()
        })

        infoWindow.open({
          anchor: newMarker,
          map: this.map,
          shouldFcosus: true
        })
      })

      this.markers.push(newMarker)
    })
  }

  async initMap() {
    const loader = new Loader('AIzaSyCBh4IdwDy9bBqTyHW8_JTRiXM8daVmvzs', {})
    const google = await loader.load()
    const zoom = 3

    const map = new google.maps.Map(document.getElementById("map"), {
      center: { lat: 40, lng: -75 },
      zoom,
      minZoom: zoom - 1,
      styles,
      gestureHandling: 'greedy',
      disableDefaultUI: true,
    })

    return map
  }

  initListeners() {
    this.animItems = [qs('p', this.content), ...qsa('li', this.content)]
    this.items = qsa('button', this.content)

    this.handleToggle = this.handleToggle.bind(this)
    this.handleItems = this.handleItems.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)

    this.toggle.addEventListener('click', this.handleToggle)
    this.close.addEventListener('click', this.handleToggle)
    this.wrapper.addEventListener('click', this.handleClickOutside)
    this.items.forEach(item => {
      item.addEventListener('click', this.handleItems)
    })

    google.maps.event.addListener(this.map, "click", event => {
      this.markers.forEach(marker => {
        marker.infoWindow.close()
      })
    })
  }

  removeListeners() {
    this.toggle.removeEventListener('click', this.handleToggle)
    this.close.removeEventListener('click', this.handleToggle)
    this.wrapper.removeEventListener('click', this.handleClickOutside)
    this.items.forEach((item, i) => {
      item.removeEventListener('click', this.handleItems)
    })

    google.maps.event.clearListeners(this.map, 'click')
    this.markers.forEach(marker => {
      google.maps.event.removeListener(marker, 'click')
    })

  }

  async checkForQueryParameters() {
    const params = new URLSearchParams(window.location.search)
    const id = params.get('id')
    const loc = params.get('loc')

    if (id && id.length) {
      const target = this.markers.find(marker => marker.id === id)

      this.map.setCenter(target.getPosition())
      this.map.setZoom(10)

      new google.maps.event.trigger( target, 'click' )
    }

    if (loc) {
      const {Geocoder} = await google.maps.importLibrary('geocoding')
      const geo = new Geocoder()
      geo.geocode({
        address: loc,
      }, (result) => {
        if (result.length && result[0].geometry.viewport) {
          this.map.fitBounds(result[0].geometry.viewport)
        }
      })
    }

  }

  createInfoWindow(marker) {
    return `
      <div class="c-info-window">
        <div class="c-info-window__avatar" style="border-color: ${marker.theme};">
          <img src="${marker.avatar}">
        </div>
        <div class="c-info-window__card">
          <div class="c-info-window__header" style="background-color: ${marker.theme};">
            <div>
              ${marker.label}
            </div>
            <div>
              ${marker.hash ? '#' + marker.hash : ''}
            </div>
          </div>
          <div class="c-info-window__content">
            <h2>${marker.title}</h2>
            <p>${marker.description}</p>
            <a href="${marker.link}">
              Learn More
            </a>
          </div>
          </div>
        </div>
      </div>
    `
  }

  handleClickOutside(e) {
    if (e.target === this.wrapper && !this.animating) {
      this.handleToggle()
    }
  }

  handleItems(e) {
    if (this.animating) return

    if (e.target.getAttribute('value').length) {
      this.activeCategory = e.target.getAttribute('value')
    } else {
      this.activeCategory = ''
    }

    this.items.forEach(item => {
      item.classList.remove('active')
    })

    e.target.classList.add('active')

    this.handleToggle()
    this.setNewMarkers()
  }

  setNewMarkers() {
    this.map.setZoom(3)
    const newMarkers = this.activeCategory.length ? this.markers.filter(marker => marker.typeSlug === this.activeCategory) : this.markers

    this.markerCluster.clearMarkers()

    newMarkers.forEach(marker => {
      this.markerCluster.addMarker(marker)
    })
  }

  handleToggle() {
    if (this.animating) return

    this.animating = true
    this.filtersOpen = !this.filtersOpen
    this.wrapper.classList[this.filtersOpen ? 'add' : 'remove']('open')
    this[this.filtersOpen ? 'filtersEnter' : 'filtersLeave']()
  }

  filtersEnter() {
    const tl = gsap.timeline({ onComplete: () => { this.animating = false }})

    tl
      .set(this.wrapper, { visibility: 'visible' })
      .fromTo(this.content, {
        autoAlpha: 0
      }, {
        duration: 0.8,
        autoAlpha: 1,
        ease: Power4.easeOut
      })
      .fromTo(this.animItems, {
        autoAlpha: 0
      }, {
        duration: 1.2,
        autoAlpha: 1,
        stagger: 0.04,
        ease: Power4.easeOut
      }, '-=0.4')
  }

  filtersLeave() {
    const tl = gsap.timeline({ onComplete: () => { this.animating = false }})

    tl
      .to(this.animItems, {
        duration: 1.2,
        autoAlpha: 0,
        stagger: 0.04,
        ease: Power4.easeOut
      })
      .to(this.content, {
        duration: 0.8,
        autoAlpha: 0,
        ease: Power4.easeOut
      }, '-=0.8')
      .set(this.wrapper, { visibility: 'hidden' })
  }
}

export default new WorldMap()
